import axios from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';

import { ValueModel } from '@/models/ValueModel';

export interface FetchSuggestionsParams {
  query: string;
  signal: AbortSignal;
  token?: string;
}

type FetchSuggestionsFn<T> = (params: FetchSuggestionsParams) => Promise<T[]>;

/**
 * Универсальная модель для автокомплита с debounce-загрузкой подсказок.
 * Поддерживает отмену устаревших запросов через AbortController.
 */
export class SuggestionsModel<T> {
  private _suggestions = new ValueModel<T[]>([]);
  private _suggestQuery = '';
  private suggestTimeout: ReturnType<typeof setTimeout> | null = null;
  private _abortController: AbortController | null = null;
  private readonly _fetchFn: FetchSuggestionsFn<T>;
  private readonly _tokenGetter?: () => string | undefined;

  constructor({
    fetchFn,
    tokenGetter,
  }: {
    fetchFn: FetchSuggestionsFn<T>;
    tokenGetter?: () => string | undefined;
  }) {
    this._fetchFn = fetchFn;
    this._tokenGetter = tokenGetter || (() => undefined);

    makeObservable<SuggestionsModel<T>, '_suggestQuery'>(this, {
      _suggestQuery: observable,
      suggestions: computed,
      suggestQuery: computed,
      setSuggestQuery: action.bound,
      fetchSuggestions: action.bound,
      refresh: action.bound,
    });
  }

  get suggestions() {
    return this._suggestions.value;
  }

  get suggestQuery() {
    return this._suggestQuery;
  }

  setSuggestQuery(query: string) {
    this._suggestQuery = query;

    if (this.suggestTimeout) clearTimeout(this.suggestTimeout);

    this.suggestTimeout = setTimeout(this.fetchSuggestions.bind(this), 300);
  }

  async fetchSuggestions() {
    this._abortController?.abort();
    this._abortController = new AbortController();

    const { signal } = this._abortController;
    const token = this._tokenGetter?.();

    try {
      const result = await this._fetchFn({
        query: this._suggestQuery.trim(),
        signal,
        token,
      });

      if (!signal.aborted) {
        this._suggestions.change(result);
      }
    } catch (e) {
      if (axios.isCancel(e) || signal.aborted) return;

      this._suggestions.change([]);
      console.error('Ошибка поиска', e);
    }
  }

  refresh() {
    if (this.suggestTimeout) clearTimeout(this.suggestTimeout);

    this.fetchSuggestions();
  }

  dispose() {
    if (this.suggestTimeout) clearTimeout(this.suggestTimeout);

    this._abortController?.abort();
    this._abortController = null;
  }
}
